import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, finalize, Observable, of, switchMap } from 'rxjs';
import { WarehouseService } from '../../../services/warehouse.service';
import { formatMoneyAmount } from '../price-field.utils';
import { ICardV2 } from '../../../interfaces/card';
import { VariantPick } from '../../warehouse/variant-picker/variant-picker.component';
import {
  CardItem,
  CardItemDiscountType,
  CardItemPayload,
  CardItemsBlock,
  CardItemsTotal,
  Named,
  StockShortage,
} from '../../warehouse/warehouse.types';
import {
  CURRENCY_OPTIONS,
  formatQuantity,
  getApiError,
  getCurrencySymbol,
  getStockShortages,
  getVariantCodes,
  getVariantTitle,
  parseNumber,
} from '../../warehouse/warehouse.utils';

/** Fields typed as text and saved on blur / Enter */
type TypedField = 'quantity' | 'price' | 'discount_value';

/**
 * Product items of a card: variant, warehouse, quantity, price, discount and the totals per currency.
 * Every change is saved right away and the whole block comes back; in the reserve column it moves the reservation,
 * once the card wrote the items off they are locked until it goes back.
 */
@Component({
  selector: 'app-board-v3-card-items',
  templateUrl: './card-items.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class BoardV3CardItemsComponent implements OnInit, OnChanges {
  @Input({ required: true }) cardId!: string;
  @Input() items: CardItem[] = [];
  @Input() totals: CardItemsTotal[] = [];
  @Input() writtenOff = false;
  @Input() cardCurrency: string | null = null;
  /** The deal amount is recalculated from the items */
  @Input() priceFromItems = false;
  @Output() changed = new EventEmitter<CardItemsBlock>();
  @Output() priceModeChange = new EventEmitter<Pick<ICardV2, 'is_price_from_items' | 'price'>>();

  private readonly warehouseService = inject(WarehouseService);
  private readonly destroyRef = inject(DestroyRef);

  /** Active warehouses plus the archived ones the items still have */
  protected warehouseOptions: Named[] = [];
  private activeWarehouses: Named[] = [];
  /** Typed text of a field until it is saved */
  protected drafts = new Map<string, Partial<Record<TypedField, string>>>();
  protected invalid = new Map<string, Set<TypedField>>();
  /**
   * Values picked but not on the item yet: a discount type before its value, a warehouse or currency while it saves.
   * After a failed change the item value shows again.
   */
  protected pending = new Map<string, CardItemPayload>();
  /** Items the last 409 complained about: missing stock or no warehouse */
  protected shortages = new Map<string, StockShortage>();
  protected missingWarehouse = new Set<string>();
  protected savingIds = new Set<string>();
  protected adding = false;
  /** Checkbox value while its request runs; afterwards the input from the card is shown again */
  protected pendingPriceFromItems: boolean | null = null;

  protected readonly currencyOptions = CURRENCY_OPTIONS;
  protected readonly discountTypes: CardItemDiscountType[] = ['PERCENT', 'AMOUNT'];
  protected readonly formatMoneyAmount = formatMoneyAmount;
  protected readonly formatQuantity = formatQuantity;
  protected readonly getVariantTitle = getVariantTitle;
  protected readonly getVariantCodes = getVariantCodes;
  protected readonly getCurrencySymbol = getCurrencySymbol;

  ngOnInit(): void {
    this.warehouseService
      .getWarehouses(false)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((warehouses) => {
        this.activeWarehouses = warehouses;
        this.updateWarehouseOptions();
      });
  }

  ngOnChanges(): void {
    this.updateWarehouseOptions();
  }

  private updateWarehouseOptions(): void {
    const archived = this.items
      .map((item) => item.warehouse)
      .filter((warehouse, index, list): warehouse is Named => {
        const first = list.findIndex((item) => item?.id === warehouse?.id) === index;
        return !!warehouse && first && !this.activeWarehouses.some((active) => active.id === warehouse.id);
      });
    this.warehouseOptions = archived.length ? [...this.activeWarehouses, ...archived] : this.activeWarehouses;
  }

  /** Items in other currencies are not part of the deal amount: there is no currency conversion */
  protected get hasItemsInOtherCurrency(): boolean {
    return this.items.some((item) => item.currency !== this.cardCurrency);
  }

  protected togglePriceFromItems(enabled: boolean): void {
    if (this.pendingPriceFromItems !== null) return;

    this.pendingPriceFromItems = enabled;
    this.warehouseService
      .setPriceFromItems(this.cardId, enabled)
      .pipe(
        finalize(() => (this.pendingPriceFromItems = null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (card) =>
          this.priceModeChange.emit({ is_price_from_items: card.is_price_from_items ?? false, price: card.price }),
        error: (error) => this.handleError(error),
      });
  }

  /** The picked value of a select, the item value when nothing is pending */
  protected current<K extends 'warehouse_id' | 'currency' | 'discount_type'>(item: CardItem, key: K): CardItem[K] {
    const pending = this.pending.get(item.id);
    return pending && key in pending ? (pending[key] as CardItem[K]) : item[key];
  }

  protected getText(item: CardItem, field: TypedField): string {
    const draft = this.drafts.get(item.id)?.[field];
    if (draft !== undefined) return draft;
    return field === 'discount_value' && !item.discount_type ? '' : String(item[field]);
  }

  protected onInput(item: CardItem, field: TypedField, value: string): void {
    this.drafts.set(item.id, { ...this.drafts.get(item.id), [field]: value });
    this.invalid.get(item.id)?.delete(field);
  }

  protected getLineTotal(item: CardItem): number {
    return item.quantity * item.price;
  }

  /** Saves the typed value of a field on blur / Enter when it differs from the item */
  protected commit(item: CardItem, field: TypedField): void {
    const text = this.drafts.get(item.id)?.[field];
    if (text === undefined) return;

    const value = text.trim() || field !== 'discount_value' ? parseNumber(text, field === 'quantity' ? 3 : 4) : 0;
    const discountType = this.current(item, 'discount_type') ?? 'PERCENT';
    const valid =
      value !== null &&
      (field === 'quantity'
        ? value > 0
        : field !== 'discount_value' || this.isDiscountAllowed(item, discountType, value));
    if (!valid) {
      this.invalid.set(item.id, (this.invalid.get(item.id) ?? new Set()).add(field));
      return;
    }

    this.clearDraft(item.id, field);
    if (field !== 'discount_value') this.change(item, field === 'quantity' ? { quantity: value } : { price: value });
    else if (value) this.change(item, { discount_type: discountType, discount_value: value });
    else if (item.discount_type) this.change(item, { discount_type: null, discount_value: 0 });
  }

  /** Without a value the type waits for one; with a value the discount is saved in the new type when it fits */
  protected setDiscountType(item: CardItem, type: CardItemDiscountType): void {
    if (this.writtenOff || this.current(item, 'discount_type') === type) return;

    this.pending.set(item.id, { ...this.pending.get(item.id), discount_type: type });
    const typed = this.drafts.get(item.id)?.discount_value;
    const value = typed !== undefined ? parseNumber(typed, 4) : item.discount_value;
    if (!value) return;

    if (!this.isDiscountAllowed(item, type, value)) {
      this.invalid.set(item.id, (this.invalid.get(item.id) ?? new Set()).add('discount_value'));
      return;
    }
    this.clearDraft(item.id, 'discount_value');
    this.change(item, { discount_type: type, discount_value: value });
  }

  protected clearDiscount(item: CardItem): void {
    this.clearDraft(item.id, 'discount_value');
    this.change(item, { discount_type: null, discount_value: 0 });
  }

  /** Saves the changed fields; the picked values show until the request is done */
  protected change(item: CardItem, payload: CardItemPayload): void {
    const keys = Object.keys(payload) as (keyof CardItemPayload)[];
    if (this.writtenOff || keys.every((key) => payload[key] === item[key as keyof CardItem])) {
      if (keys.includes('discount_type')) this.pending.delete(item.id);
      return;
    }

    this.pending.set(item.id, { ...this.pending.get(item.id), ...payload });
    this.savingIds.add(item.id);
    this.handle(
      this.warehouseService.updateCardItem(this.cardId, item.id, payload).pipe(
        finalize(() => {
          this.savingIds.delete(item.id);
          this.pending.delete(item.id);
        }),
      ),
    );
  }

  protected add(pick: VariantPick): void {
    if (this.adding || this.writtenOff) return;

    const existing = this.items.find((item) => item.variant_id === pick.id);
    if (existing) return this.change(existing, { quantity: existing.quantity + 1 });

    this.adding = true;
    this.handle(
      this.warehouseService
        .addCardItem(this.cardId, { variant_id: pick.id, quantity: 1 })
        .pipe(finalize(() => (this.adding = false))),
    );
  }

  protected remove(item: CardItem): void {
    if (this.writtenOff || this.savingIds.has(item.id)) return;

    this.handle(
      this.warehouseService.confirm('stock.card_items.delete_confirm', { name: getVariantTitle(item.variant) }).pipe(
        switchMap(() => {
          this.savingIds.add(item.id);
          return this.warehouseService.removeCardItem(this.cardId, item.id);
        }),
        finalize(() => this.savingIds.delete(item.id)),
      ),
    );
  }

  /** PERCENT: 0-100 of the line; AMOUNT: at most quantity × price, like the backend checks */
  private isDiscountAllowed(item: CardItem, type: CardItemDiscountType, value: number): boolean {
    return value >= 0 && value <= (type === 'PERCENT' ? 100 : this.getLineTotal(item));
  }

  private handle(request$: Observable<CardItemsBlock>): void {
    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (block) => {
        this.shortages.clear();
        this.missingWarehouse.clear();
        this.changed.emit(block);
      },
      error: (error) => this.handleError(error),
    });
  }

  private handleError(error: unknown): void {
    this.shortages = new Map(
      getStockShortages(error).flatMap((shortage) =>
        shortage.card_item_ids.map((itemId) => [itemId, shortage] as const),
      ),
    );
    const { code, details } = getApiError(error);
    const itemIds = details?.['card_item_ids'];
    this.missingWarehouse = new Set(
      code === 'CARD_ITEM_WAREHOUSE_REQUIRED' && Array.isArray(itemIds) ? (itemIds as string[]) : [],
    );
    this.warehouseService.error(
      error,
      (variantId) => getVariantTitle(this.items.find((item) => item.variant_id === variantId)?.variant) || null,
    );
  }

  private clearDraft(itemId: string, field: TypedField): void {
    const { [field]: cleared, ...rest } = this.drafts.get(itemId) ?? {};
    if (cleared !== undefined && Object.keys(rest).length) this.drafts.set(itemId, rest);
    else if (cleared !== undefined) this.drafts.delete(itemId);
  }
}
